import { reactive } from 'vue';
import type { BundleInfo, DownloadOptions } from '@capgo/capacitor-updater';

const launchKey = 'eqover-ota-link-consumed-v1';
const ownedKey = 'eqover-ota-link-owned-v1:';
const attemptKey = 'eqover-ota-link-attempt-v1:';

export function createNativeOtaDeepLinkController(dependencies: {
  resolve: (version: string, signal: AbortSignal) => Promise<DownloadOptions>;
  current: () => Promise<{ bundle: BundleInfo; native?: string }>;
  download: (options: DownloadOptions) => Promise<BundleInfo>;
  set: (options: { id: string }) => Promise<void>;
  delete: (options: { id: string }) => Promise<void>;
  storage: () => Pick<Storage, 'getItem' | 'setItem'>;
  launchStorage: () => Pick<Storage, 'getItem' | 'setItem'>;
}) {
  const state = reactive({
    open: false, phase: 'idle' as 'idle' | 'resolving' | 'downloading' | 'applying' | 'complete' | 'error',
    version: '', percent: 0, message: '', confirmingCancel: false,
  });
  type Operation = { cancelled: boolean; abort: AbortController; confirmation?: () => void };
  let operation: Operation | undefined;
  const ownedVersions = new Set<string>();

  function ownsVersion(version: string): boolean {
    try { return ownedVersions.has(version) || dependencies.storage().getItem(`${ownedKey}${version}`) === '1'; }
    catch { return true; } // Sin almacenamiento durable, no permitir una aplicación automática.
  }

  function blocksAutomatic(version: string): boolean {
    try {
      // Conservar la versión elegida durante esta sesión, también tras set() o rollback.
      return state.open || ownsVersion(version) || dependencies.launchStorage().getItem(launchKey) !== null;
    } catch { return true; }
  }

  function progress(percent: number, bundle: BundleInfo) {
    if (state.open && state.phase === 'downloading' && !operation?.cancelled && bundle.version === state.version) {
      if (Number.isFinite(percent)) state.percent = Math.max(state.percent, Math.min(100, Math.max(0, Math.round(percent))));
    }
  }

  function requestCancel() {
    if (state.open && state.phase !== 'applying') state.confirmingCancel = true;
  }

  function confirmCancel(cancel: boolean) {
    if (!state.confirmingCancel) return;
    state.confirmingCancel = false;
    if (cancel) {
      if (operation) {
        operation.cancelled = true;
        operation.abort.abort();
      }
      state.open = false;
    }
    operation?.confirmation?.();
  }

  async function checkpoint(run: Operation): Promise<boolean> {
    if (state.confirmingCancel && !run.cancelled) {
      await new Promise<void>((resolve) => { run.confirmation = resolve; });
      run.confirmation = undefined;
    }
    return !run.cancelled;
  }

  async function isLoaded(version: string): Promise<boolean> {
    const current = await dependencies.current();
    return (current.bundle.id === 'builtin' ? current.native ?? current.bundle.version : current.bundle.version).trim() === version;
  }

  async function start(requestedVersion: string, launch = false): Promise<void> {
    if (state.open || operation) return;
    const run: Operation = { cancelled: false, abort: new AbortController() };
    operation = run;
    Object.assign(state, { open: true, phase: 'resolving', version: requestedVersion, percent: 0, message: '', confirmingCancel: false });
    let downloaded: BundleInfo | undefined;
    try {
      const storage = dependencies.storage();
      const launchStorage = dependencies.launchStorage();
      // getLaunchUrl() puede repetir el mismo intent después del set() o de un rollback.
      if (launch && launchStorage.getItem(launchKey) === requestedVersion) {
        state.open = false;
        return;
      }
      launchStorage.setItem(launchKey, requestedVersion);
      const release = await dependencies.resolve(requestedVersion, run.abort.signal);
      if (!await checkpoint(run)) return;
      state.version = release.version;
      ownedVersions.add(release.version);
      storage.setItem(`${ownedKey}${release.version}`, '1');
      if (await isLoaded(release.version)) {
        if (!await checkpoint(run)) return;
        Object.assign(state, { phase: 'complete', percent: 100, message: 'Esta versión ya está instalada.' });
        return;
      }
      if (!await checkpoint(run)) return;
      if (storage.getItem(`${attemptKey}${release.version}`) === '1') {
        throw new Error('Ya se intentó aplicar esta versión. Se detuvo el reintento para evitar un ciclo de reinicios.');
      }
      state.phase = 'downloading';
      downloaded = await dependencies.download(release);
      if (!await checkpoint(run)) return;
      if (downloaded.version !== release.version || !['pending', 'success'].includes(downloaded.status) || downloaded.id === 'builtin') {
        throw new Error('La descarga OTA no produjo un bundle válido para la versión solicitada.');
      }
      state.percent = 100;
      if (await isLoaded(release.version)) {
        if (!await checkpoint(run)) return;
        Object.assign(state, { phase: 'complete', message: 'Esta versión ya está instalada.' });
        return;
      }
      if (!await checkpoint(run)) return;
      // Persistir ANTES de set(): no se puede ejecutar lógica después de destruir el contexto JS.
      storage.setItem(`${attemptKey}${release.version}`, '1');
      storage.setItem('eqover-ota-automatic-attempt-v1', release.version);
      state.phase = 'applying';
      await dependencies.set({ id: downloaded.id });
    } catch (error) {
      if (!run.cancelled) {
        state.phase = 'error';
        state.message = error instanceof Error ? error.message : 'No fue posible actualizar la aplicación.';
      }
    } finally {
      // Capgo no expone abort de transporte. Una descarga cancelada nunca se agenda ni se aplica;
      // si termina en segundo plano, eliminar su bundle y suprimir sus eventos tardíos.
      if (downloaded && state.phase !== 'applying') {
        await dependencies.delete({ id: downloaded.id }).catch(() => undefined);
      }
      if (operation === run) operation = undefined;
    }
  }

  return { state, start, progress, ownsVersion, blocksAutomatic, requestCancel, confirmCancel };
}
